import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';



// Default app title - exported for consistent usage
export const DEFAULT_TITLE = 'Mediatropy HR';
export const TITLE_SUFFIX = ` | ${DEFAULT_TITLE}`;
export const DEV_TITLE_PREFIX = 'DEV | ';

export const isDevelopmentTitleMode = ({
    nodeEnv = process.env.NODE_ENV,
    viteEnv = process.env.VITE_ENV,
    viteEnvironment = process.env.VITE_ENVIRONMENT
} = {}) => {
    return [nodeEnv, viteEnv, viteEnvironment]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase() === 'development');
};

export const formatDocumentTitle = (title, environment) => {
    const baseTitle = title || DEFAULT_TITLE;

    if (!isDevelopmentTitleMode(environment) || /^DEV\b/i.test(baseTitle)) {
        return baseTitle;
    }

    return `${DEV_TITLE_PREFIX}${baseTitle}`;
};

// Create context
const PageTitleContext = createContext();

/**
 * PageTitleProvider - Manages document title globally
 *
 * Provides a hybrid system where:
 * 1. Default titles are set automatically based on the current section/route
 * 2. Components can override with specific titles (e.g., project names)
 * 3. Overrides are automatically cleared when components unmount
 */
export const PageTitleProvider = ({ children }) => {
    // The base/default title for the current section
    const [defaultTitle, setDefaultTitle] = useState(DEFAULT_TITLE);

    // Override title set by child components (takes precedence over default)
    const [overrideTitle, setOverrideTitle] = useState(null);

    // Track active overrides by component ID with timestamps to handle race conditions
    const activeOverridesRef = useRef(new Map());

    // Update document.title whenever titles change
    useEffect(() => {
        try {
            const title = overrideTitle || defaultTitle || DEFAULT_TITLE;
            document.title = formatDocumentTitle(title);
        } catch (error) {
            logger.error('Error setting document title:', error);
            document.title = formatDocumentTitle(DEFAULT_TITLE);
        }
    }, [defaultTitle, overrideTitle]);

    /**
     * Set the default title for the current section
     * Called by HomeContent when section changes
     */
    const setPageTitle = useCallback((title) => {
        setDefaultTitle(title || DEFAULT_TITLE);
    }, []);

    /**
     * Override the title temporarily (e.g., for detail pages with specific names)
     * Uses timestamp tracking to handle race conditions when multiple components set overrides
     * Returns a cleanup function that clears the override
     */
    const setTitleOverride = useCallback((title, componentId) => {
        if (title) {
            const timestamp = Date.now();
            activeOverridesRef.current.set(componentId, { title, timestamp });

            // Set the most recent override as the active title
            const overrides = Array.from(activeOverridesRef.current.values());
            const latest = overrides.sort((a, b) => b.timestamp - a.timestamp)[0];
            setOverrideTitle(latest.title);
        }

        // Return cleanup function
        return () => {
            activeOverridesRef.current.delete(componentId);

            // If there are still other overrides, use the most recent one
            if (activeOverridesRef.current.size > 0) {
                const overrides = Array.from(activeOverridesRef.current.values());
                const latest = overrides.sort((a, b) => b.timestamp - a.timestamp)[0];
                setOverrideTitle(latest.title);
            } else {
                setOverrideTitle(null);
            }
        };
    }, []);

    /**
     * Clear any override and revert to default title
     */
    const clearOverride = useCallback(() => {
        setOverrideTitle(null);
        activeOverridesRef.current.clear();
    }, []);

    const value = {
        currentTitle: overrideTitle || defaultTitle,
        defaultTitle,
        overrideTitle,
        setPageTitle,
        setTitleOverride,
        clearOverride
    };

    return (
        <PageTitleContext.Provider value={value}>
            {children}
        </PageTitleContext.Provider>
    );
};

/**
 * Hook to access page title context
 */
export const usePageTitleContext = () => {
    const context = useContext(PageTitleContext);
    if (!context) {
        throw new Error('usePageTitleContext must be used within a PageTitleProvider');
    }
    return context;
};

// Counter for generating unique component IDs (guaranteed uniqueness)
let componentIdCounter = 0;

/**
 * Hook for components to set/override the page title
 *
 * Usage:
 *   // Simple usage - just set a title
 *   usePageTitle('My Page Title');
 *
 *   // With suffix (adds " | Mediatropy HR")
 *   usePageTitle('Project Details', true); // Results in "Project Details | Mediatropy HR"
 *
 *   // Dynamic title based on data
 *   usePageTitle(project ? `${project.name} | Project` : null);
 *
 * The title is automatically cleared when the component unmounts.
 */
export const usePageTitle = (title, appendSuffix = false) => {
    const { setTitleOverride, setPageTitle } = usePageTitleContext();
    const componentId = useRef(`page-title-${++componentIdCounter}`);

    useEffect(() => {
        // Only set override if title is a non-empty string
        if (title !== null && title !== undefined && title !== '') {
            const fullTitle = appendSuffix ? `${title}${TITLE_SUFFIX}` : title;
            const cleanup = setTitleOverride(fullTitle, componentId.current);
            return cleanup;
        }
        // Return empty cleanup if no title - the default section title will show
        return undefined;
    }, [title, appendSuffix, setTitleOverride]);

    // Return setPageTitle for components that want to set the default (non-override) title
    return { setPageTitle };
};

/**
 * Hook specifically for setting default section titles
 * Used by HomeContent to set titles based on current route
 */
export const useDefaultPageTitle = () => {
    const { setPageTitle, clearOverride } = usePageTitleContext();

    const setDefaultTitle = useCallback((title) => {
        clearOverride(); // Clear any override when navigating to a new section
        setPageTitle(title);
    }, [setPageTitle, clearOverride]);

    return { setDefaultTitle };
};

export default PageTitleContext;
